import ERROR_SOUND from '@assets/audio/windows_xp_error.mp3';

import baseApi from '@store/api/baseApi';

import {
	setIsCycleStart,
	setIsLocationScannedBefore,
	setIsUndo,
	setLocationResponseFromLocationScan,
	setLocationUpdateResponse,
	setRowQrCode,
	setScannedItems,
	setScannedSampleIds,
	setSearchValue,
	setSelectedProductIds
} from '@/features/Scan/redux/scanSlice';
import { toastError, toastSuccess, toastWarning } from '@/components/Toast';
import { fetchTotalLikeCount } from '@/store/favourite/actions';

const errorSound = new Audio(ERROR_SOUND);

export const scanApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchScannedItemByQRCode: builder.query({
			query: (sampleIdentifierNo) => ({
				url: `/product-sample-inventory/${sampleIdentifierNo}`,
				method: 'GET'
			}),

			async onQueryStarted(_args, { queryFulfilled, dispatch, getState }) {
				const { rowQrCode } = getState().scanStore;
				const {
					scannedItems,
					scannedSampleIds,
					selectedProductIds,
					isCycleStart,
					isLocationScannedBefore
				} = getState().scanStore;
				try {
					const { data } = await queryFulfilled;

					const scannedProduct = data;
					const sampleIds =
						scannedProduct?.sampleInventoryItemResponseList?.map((item) => item?.id) || [];
					if (!isCycleStart && !isLocationScannedBefore) {
						// Reset scan items if user updates the location and scan again.
						dispatch(setScannedItems([scannedProduct]));
						dispatch(setSelectedProductIds([scannedProduct?.id]));
						dispatch(setScannedSampleIds(sampleIds));
					} else {
						// Add new scanned item to existing list when location is not updated (not once).
						const updatedScannedItems = {
							...scannedProduct,
							sl: scannedItems.length + 1,
							sampleId: scannedProduct?.sampleInventoryItemResponseList?.[0]?.id,
							isFound: true
						};

						dispatch(setScannedItems([updatedScannedItems, ...scannedItems]));
						dispatch(setSelectedProductIds([scannedProduct?.id, ...selectedProductIds]));
						dispatch(setScannedSampleIds([...sampleIds, ...scannedSampleIds]));
					}

					// Reset location update and search states
					dispatch(setSearchValue(''));
					dispatch(setRowQrCode(''));
				} catch ({ error }) {
					if (error.status === 400) {
						// Create a new scanned item if not found
						const getSampleId = rowQrCode?.split(';')?.[0] || '';
						const getDesignNumber = rowQrCode?.split(';')?.[1] || '';
						const isDuplicateSample = scannedItems.some((item) => item.sampleId === getSampleId);

						if (isDuplicateSample) {
							toastWarning('Sample already scanned');
						} else {
							dispatch(
								setScannedItems([
									...scannedItems,
									{
										sampleId: getSampleId,
										designNumber: getDesignNumber,
										isFound: false,
										sl: scannedItems.length + 1,
										sampleInventoryItemResponseList: [{ sampleId: getSampleId }]
									}
								])
							);
						}
					}

					const errorMessage = error?.data?.message || 'No Sample Found';
					toastError(errorMessage);
					errorSound.play();
					dispatch(setSearchValue(''));
				}
			}
		}),

		updateProductLocationWithQRCode: builder.mutation({
			query: ({ sampleIds = [], locationIdentifierNo, remarks }) => ({
				url: `/product-sample-inventory/update-location?locationIdentifierNo=${encodeURIComponent(locationIdentifierNo)}`,
				method: 'PUT',
				body: {
					sampleIdList: [...sampleIds],
					remarks
				}
			}),

			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						setLocationUpdateResponse({
							...data,
							isFound: true
						})
					);
					dispatch(setSearchValue(''));
					dispatch(setIsUndo(false));
					toastSuccess('Location updated');
					dispatch(setIsCycleStart(false));
				} catch ({ error }) {
					const errorMessage = error?.data?.message;
					if (error.status === 400) {
						const locationId = errorMessage?.split(' ')?.[0] || '';
						// if location id is not found
						dispatch(
							setLocationUpdateResponse({
								locationId,
								isFound: false
							})
						);
						dispatch(setIsCycleStart(true));
						dispatch(setIsLocationScannedBefore(false));
					}
					dispatch(setSearchValue(''));
					dispatch(setIsUndo(false));
					toastWarning(
						`This location ID is not found. Please click on the action button "Add this new location" if you want to add it.`
					);
					errorSound.play();
				}
			}
		}),

		undoLocationUpdate: builder.mutation({
			query: ({ sampleIds = [] }) => ({
				url: `/product-sample-inventory/undo-location`,
				method: 'PUT',
				body: [...sampleIds]
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					await queryFulfilled;
					dispatch(setIsUndo(true));
				} catch (error) {
					toastError(error?.error?.data?.message);
				}
			}
		}),

		fetchProductTag: builder.query({
			query: ({ params }) => ({
				url: `/tags/all`,
				params: { ...params }
			})
		}),

		searchLocation: builder.query({
			query: ({ locationIdentifierNo, page }) => ({
				url: `/product-sample-inventory/location/${encodeURIComponent(locationIdentifierNo)}/samples`,
				method: 'GET',
				params: { page, size: 50 }
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch, getState }) {
				try {
					const { data } = await queryFulfilled;
					const { scannedItems, selectedProductIds, scannedSampleIds } = getState().scanStore;
					const lastSerialNumber = scannedItems.reduce((maxSl, item) => {
						return item.sl > maxSl ? item.sl : maxSl;
					}, 0);

					const result = data?.productResponsePage?.data?.map((item, index) => ({
						...item,
						sl: lastSerialNumber + index + 1,
						isFound: data?.productResponsePage?.data?.length > 0
					}));

					const locationResponse = {
						...data,
						locationResponse: { ...data?.locationResponse, isFound: true }
					};

					dispatch(setScannedItems([...scannedItems, ...result]));
					dispatch(setLocationResponseFromLocationScan(locationResponse));
					dispatch(
						setSelectedProductIds([...selectedProductIds, ...result.map((item) => item.id)])
					);
					dispatch(
						setScannedSampleIds([
							...scannedSampleIds,
							...result.map((item) => item?.sampleInventoryItemResponseList?.[0]?.id)
						])
					);
					dispatch(setSearchValue(''));
					dispatch(setIsCycleStart(true));
					dispatch(setIsLocationScannedBefore(true));
				} catch ({ error }) {
					if (error?.status === 404 || error?.status === 400) {
						const locationId = error?.data?.message?.split(' ')?.[0] || '';
						const { locationResponseFromLocationScan } = getState().scanStore;
						dispatch(
							setLocationResponseFromLocationScan({
								...locationResponseFromLocationScan,
								locationResponse: { isFound: false, locationId }
							})
						);
						toastWarning(
							`This location ID is not found. Please click on the action button "Add this new location" if you want to add it.`
						);
					} else {
						toastError(error?.data?.message);
					}
					dispatch(setIsCycleStart(false));
					dispatch(setIsLocationScannedBefore(false));
					dispatch(setSearchValue(''));
					errorSound.play();
				}
			}
		}),

		replaceLocation: builder.mutation({
			query: ({ newLocationIdentifierNo, currentLocationId }) => ({
				url: `/product-sample-inventory/replace-location`,
				method: 'PUT',
				params: {
					currentLocationId,
					newLocationIdentifierNo
				}
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch, getState }) {
				try {
					const response = await queryFulfilled;
					dispatch(
						setLocationUpdateResponse({
							...response?.data?.newLocation,
							isFound: true,
							isReplaced: true
						})
					);
					dispatch(
						setLocationResponseFromLocationScan({
							locationResponse: {
								...response?.data?.prevLocation,
								isFound: true
							}
						})
					);
					dispatch(setScannedSampleIds(response?.data?.replacedItemIds));
					dispatch(setIsLocationScannedBefore(false));
					dispatch(setIsCycleStart(false));
					dispatch(setSearchValue(''));
					dispatch(setIsUndo(false));
					toastSuccess('Location Replaced successfully');
				} catch ({ error }) {
					const locationId = error?.data?.message?.split(' ')?.[0] || '';
					const { locationResponseFromLocationScan } = getState().scanStore;
					if (error?.status === 404 || error?.status === 400) {
						dispatch(
							setLocationResponseFromLocationScan({
								locationResponse: {
									isFound: false,
									isReplaced: false,
									locationId,
									id: locationResponseFromLocationScan?.locationResponse?.id
								}
							})
						);

						toastWarning(error?.data?.message);
					} else {
						toastError(error?.data?.message);
					}
					dispatch(setSearchValue(''));
					dispatch(setIsLocationScannedBefore(true));
					dispatch(setIsCycleStart(true));
				}
			}
		}),

		addCommentToProduct: builder.mutation({
			query: ({ id, params }) => ({
				url: `product/collection-remarks/${id}`,
				method: 'POST',
				params: { ...params }
			})
		}),

		likeProduct: builder.mutation({
			query: ({ productIds, responseType, folderId }) => ({
				url: `/product/like-with-request-body`,
				method: 'POST',
				body: {
					productIds,
					folderId,
					responseType
				}
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					const { data } = await queryFulfilled;
					dispatch({
						type: 'LIKE_DESIGN_SUCCESS'
					});
					dispatch({
						type: 'TOTAL_LIKED_PRODUCT_SUCCESS',
						payload: data.payload
					});
					dispatch(fetchTotalLikeCount());
				} catch (error) {
					toastError(error?.data?.message);
					dispatch({ type: 'LIKE_DESIGN_FAILURE' });
				}
			}
		}),
		productDevelopmentAdd: builder.mutation({
			query: ({ body }) => ({
				url: `/product-development/add`,
				method: 'POST',
				body
			})
		}),
		getProductActionList: builder.query({
			query: ({ params }) => ({
				url: `/product_action/list`,
				params,
				method: 'GET'
			}),
			keepUnusedDataFor: 0
		}),
		productActionAdd: builder.mutation({
			query: ({ body }) => ({
				url: `/product_action/add`,
				method: 'POST',
				body
			})
		})
	})
});

export const {
	useLazyFetchScannedItemByQRCodeQuery,
	useUpdateProductLocationWithQRCodeMutation,
	useLazySearchLocationQuery,
	useUndoLocationUpdateMutation,
	useLazyFetchProductTagQuery,
	useSearchLocationQuery,
	useAddCommentToProductMutation,
	useReplaceLocationMutation,
	useLikeProductMutation,
	useProductDevelopmentAddMutation,
	useGetProductActionListQuery,
	useProductActionAddMutation
} = scanApi;
